import {
  type FlowKind,
  type FlowOfFunds,
  type FlowRole,
  flowKindOf,
  nodesByName,
  roleOf,
} from './flowModel'

export interface FlowSide {
  amount: number
  count: number
}

export interface NodeTotals {
  in: FlowSide
  out: FlowSide
}

export interface KindTotals extends FlowSide {
  sources: Set<string>
  targets: Set<string>
}

const emptySide = (): FlowSide => ({ amount: 0, count: 0 })

function addTo(side: FlowSide, amount: number, count: number) {
  side.amount += amount
  side.count += count
}

export function nodeTotals(flow: FlowOfFunds): Map<string, NodeTotals> {
  const totals = new Map<string, NodeTotals>(
    flow.nodes.map((node) => [
      node.name,
      { in: emptySide(), out: emptySide() },
    ]),
  )
  for (const link of flow.links) {
    const count = link.count ?? 0
    const source = totals.get(link.source)
    const target = totals.get(link.target)
    if (source) addTo(source.out, link.value, count)
    if (target) addTo(target.in, link.value, count)
  }
  return totals
}

export function kindTotals(flow: FlowOfFunds): Record<FlowKind, KindTotals> {
  const emptyKind = (): KindTotals => ({
    ...emptySide(),
    sources: new Set(),
    targets: new Set(),
  })
  const totals: Record<FlowKind, KindTotals> = {
    in: emptyKind(),
    kept: emptyKind(),
    between: emptyKind(),
    out: emptyKind(),
  }
  const byName = nodesByName(flow)
  for (const link of flow.links) {
    const kind = totals[flowKindOf(link, byName)]
    addTo(kind, link.value, link.count ?? 0)
    kind.sources.add(link.source)
    kind.targets.add(link.target)
  }
  return totals
}

// The side of each column that faces outside the own accounts. A column
// with nothing on that side (an account seen only in transfers) falls
// back to its other side.
const OUTWARD_SIDE: Record<FlowRole, keyof NodeTotals> = {
  payer: 'out',
  received: 'in',
  spent: 'out',
  account: 'in',
  from: 'out',
  to: 'in',
  payee: 'in',
}

export function columnTotal(
  flow: FlowOfFunds,
  role: FlowRole,
  totals: Map<string, NodeTotals> = nodeTotals(flow),
): number {
  const outward = OUTWARD_SIDE[role]
  const inward = outward === 'in' ? 'out' : 'in'
  const columnNodes = flow.nodes.filter((node) => roleOf(node) === role)
  const sumSide = (side: keyof NodeTotals) =>
    columnNodes.reduce(
      (sum, node) => sum + (totals.get(node.name)?.[side].amount ?? 0),
      0,
    )
  return sumSide(outward) || sumSide(inward)
}
